import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { checklistsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Trash2, Loader2, ClipboardList, Settings, Eye, Download, ImageIcon } from 'lucide-react';
import jsPDF from 'jspdf';

const STATUS_CONFIG = {
  '': { label: 'No Status', color: 'bg-transparent', textColor: 'text-muted-foreground', rgb: [255, 255, 255] },
  work_going: { label: 'Work Going', color: 'bg-emerald-600', textColor: 'text-white', rgb: [5, 150, 105] },
  pending: { label: 'Pending', color: 'bg-red-600', textColor: 'text-white', rgb: [220, 38, 38] },
  decision_pending: { label: 'Decision Pending', color: 'bg-yellow-400', textColor: 'text-black', rgb: [250, 204, 21] },
};

function exportChecklistToPDF(checklist) {
  const dates = checklist.start_date && checklist.end_date
    ? getDatesBetween(checklist.start_date, checklist.end_date)
    : [];
  const items = checklist.items || [];
  
  const descColWidth = 80;
  const dateColWidth = 14;
  const rowHeight = 8;
  const headerHeight = 12;
  const marginLeft = 10;
  const marginTop = 20;
  
  const totalWidth = descColWidth + (dates.length * dateColWidth);
  const orientation = totalWidth > 190 ? 'l' : 'p';
  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  
  // Title
  doc.setFontSize(14);
  doc.text(checklist.title || 'Checklist', marginLeft, marginTop - 6);
  
  let y = marginTop;
  
  // Header row
  doc.setFontSize(7);
  doc.setFillColor(240, 240, 240);
  doc.rect(marginLeft, y, descColWidth, headerHeight, 'F');
  doc.setDrawColor(180, 180, 180);
  doc.rect(marginLeft, y, descColWidth, headerHeight, 'S');
  doc.setTextColor(80, 80, 80);
  doc.text(checklist.title || 'Description', marginLeft + 2, y + headerHeight / 2 + 1);
  
  dates.forEach((dateStr, i) => {
    const x = marginLeft + descColWidth + (i * dateColWidth);
    const { day, dayName, isSunday } = formatDateHeader(dateStr);
    if (isSunday) {
      doc.setFillColor(230, 230, 230);
    } else {
      doc.setFillColor(240, 240, 240);
    }
    doc.rect(x, y, dateColWidth, headerHeight, 'F');
    doc.rect(x, y, dateColWidth, headerHeight, 'S');
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(5);
    doc.text(dayName, x + dateColWidth / 2, y + 4, { align: 'center' });
    doc.setFontSize(7);
    doc.setTextColor(50, 50, 50);
    doc.text(String(day), x + dateColWidth / 2, y + 9, { align: 'center' });
  });
  
  y += headerHeight;
  
  // Data rows
  items.forEach((item) => {
    // Check if we need a new page
    if (y + rowHeight > (orientation === 'l' ? 200 : 280)) {
      doc.addPage();
      y = marginTop;
    }
    
    // Description cell
    doc.setFillColor(255, 255, 255);
    doc.rect(marginLeft, y, descColWidth, rowHeight, 'F');
    doc.setDrawColor(180, 180, 180);
    doc.rect(marginLeft, y, descColWidth, rowHeight, 'S');
    doc.setFontSize(7);
    doc.setTextColor(30, 30, 30);
    const descText = item.description || '';
    doc.text(descText.substring(0, 50), marginLeft + 2, y + rowHeight / 2 + 1);
    
    // Status cells
    dates.forEach((dateStr, i) => {
      const x = marginLeft + descColWidth + (i * dateColWidth);
      const status = item.statuses?.[dateStr] || '';
      const cfg = STATUS_CONFIG[status] || STATUS_CONFIG[''];
      
      if (status && cfg.rgb) {
        doc.setFillColor(...cfg.rgb);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(x, y, dateColWidth, rowHeight, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.rect(x, y, dateColWidth, rowHeight, 'S');
    });
    
    y += rowHeight;
  });
  
  // Legend
  y += 8;
  if (y > (orientation === 'l' ? 190 : 270)) {
    doc.addPage();
    y = marginTop;
  }
  
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  const legendItems = Object.entries(STATUS_CONFIG).filter(([k]) => k !== '');
  legendItems.forEach(([key, cfg], i) => {
    const lx = marginLeft + (i * 50);
    doc.setFillColor(...cfg.rgb);
    doc.rect(lx, y, 6, 4, 'F');
    doc.setDrawColor(180, 180, 180);
    doc.rect(lx, y, 6, 4, 'S');
    doc.setTextColor(80, 80, 80);
    doc.text(cfg.label, lx + 8, y + 3);
  });
  
  doc.save(`${(checklist.title || 'checklist').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
}

function getDatesBetween(startDate, endDate) {
  const dates = [];
  const current = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function formatDateHeader(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDate();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayName = days[d.getDay()];
  return { day, dayName, isSunday: d.getDay() === 0 };
}

function ChecklistGrid({ checklist, onRefresh, canEdit }) {
  const dates = checklist.start_date && checklist.end_date
    ? getDatesBetween(checklist.start_date, checklist.end_date)
    : [];
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemImage, setNewItemImage] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [viewImage, setViewImage] = useState(null);

  const handleAddItem = async () => {
    if (!newItemDesc.trim()) return;
    setAddingItem(true);
    try {
      await checklistsAPI.addItem(checklist.checklist_id, { description: newItemDesc.trim(), image_url: newItemImage.trim() });
      setNewItemDesc('');
      setNewItemImage('');
      onRefresh();
    } catch (err) {
      console.error('Failed to add item:', err);
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await checklistsAPI.deleteItem(checklist.checklist_id, itemId);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleStatusChange = async (itemId, dateKey, newStatus) => {
    try {
      await checklistsAPI.updateItemStatus(checklist.checklist_id, itemId, {
        date: dateKey,
        status: newStatus,
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleSaveDescription = async (itemId) => {
    try {
      await checklistsAPI.updateItem(checklist.checklist_id, itemId, { description: editDesc });
      setEditingItem(null);
      onRefresh();
    } catch (err) {
      console.error('Failed to update item:', err);
    }
  };

  return (
    <div className="space-y-4" data-testid={`checklist-grid-${checklist.checklist_id}`}>
      {/* Grid Table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left p-2 font-medium min-w-[250px] border-r border-border sticky left-0 bg-muted/30 z-10">
                {checklist.title || 'Checklist'}
              </th>
              {dates.map((dateStr) => {
                const { day, dayName, isSunday } = formatDateHeader(dateStr);
                return (
                  <th
                    key={dateStr}
                    className={`p-1 text-center font-medium min-w-[42px] border-r border-border last:border-r-0 ${
                      isSunday ? 'bg-muted/60' : ''
                    }`}
                  >
                    <div className="text-[10px] text-muted-foreground leading-tight">{dayName}</div>
                    <div className="text-xs leading-tight">{day}</div>
                  </th>
                );
              })}
              {canEdit && (
                <th className="p-1 min-w-[36px] text-center font-medium"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {(checklist.items || []).map((item) => (
              <tr key={item.item_id} className="border-b border-border last:border-b-0 hover:bg-muted/20" data-testid={`checklist-item-${item.item_id}`}>
                <td className="p-2 border-r border-border sticky left-0 bg-background z-10">
                  {editingItem === item.item_id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        className="h-7 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveDescription(item.item_id);
                          if (e.key === 'Escape') setEditingItem(null);
                        }}
                      />
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleSaveDescription(item.item_id)}>
                        Save
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {item.image_url && (
                        <button onClick={() => setViewImage(item.image_url)} className="shrink-0">
                          <img src={item.image_url} alt="" className="w-6 h-6 rounded object-cover border hover:opacity-80 transition-opacity" />
                        </button>
                      )}
                      <span
                        className={`text-sm ${canEdit ? 'cursor-pointer hover:underline' : ''}`}
                        onClick={() => {
                          if (canEdit) {
                            setEditingItem(item.item_id);
                            setEditDesc(item.description);
                          }
                        }}
                        data-testid={`item-desc-${item.item_id}`}
                      >
                        {item.description || '(empty)'}
                      </span>
                    </div>
                  )}
                </td>
                {dates.map((dateStr) => {
                  const status = item.statuses?.[dateStr] || '';
                  const config = STATUS_CONFIG[status] || STATUS_CONFIG[''];
                  return (
                    <td
                      key={dateStr}
                      className="p-0 border-r border-border last:border-r-0"
                    >
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={`w-full h-8 ${config.color} hover:opacity-80 transition-opacity cursor-pointer`}
                              data-testid={`cell-${item.item_id}-${dateStr}`}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="min-w-[160px]">
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                              <DropdownMenuItem
                                key={key}
                                onClick={() => handleStatusChange(item.item_id, dateStr, key)}
                                className="flex items-center gap-2 cursor-pointer"
                                data-testid={`status-option-${key || 'empty'}`}
                              >
                                <div className={`w-4 h-4 rounded-sm ${cfg.color} ${key === '' ? 'border border-border' : ''}`} />
                                <span>{cfg.label}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div className={`w-full h-8 ${config.color}`} />
                      )}
                    </td>
                  );
                })}
                {canEdit && (
                  <td className="p-0 text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteItem(item.item_id)}
                      data-testid={`delete-item-${item.item_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {/* Add item row */}
            {canEdit && (
              <tr className="border-b border-border">
                <td className="p-2 border-r border-border sticky left-0 bg-background z-10" colSpan={dates.length + 2}>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newItemDesc}
                      onChange={(e) => setNewItemDesc(e.target.value)}
                      placeholder="Add new work item..."
                      className="h-8 text-sm flex-1 max-w-xs"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
                      data-testid="new-item-input"
                    />
                    <Input
                      value={newItemImage}
                      onChange={(e) => setNewItemImage(e.target.value)}
                      placeholder="Image URL (optional)"
                      className="h-8 text-sm flex-1 max-w-xs"
                      data-testid="new-item-image-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleAddItem}
                      disabled={addingItem || !newItemDesc.trim()}
                      className="h-8"
                      data-testid="add-item-btn"
                    >
                      {addingItem ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      <span className="ml-1">Add</span>
                    </Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="checklist-legend">
        {Object.entries(STATUS_CONFIG).filter(([k]) => k !== '').map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-sm ${cfg.color}`} />
            <span className="text-muted-foreground">{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Image Viewer */}
      {viewImage && (
        <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Reference Image</DialogTitle></DialogHeader>
            <img src={viewImage} alt="Reference" className="w-full rounded" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function ProjectChecklists({ projectId }) {
  const { isAdmin, isStaff, user } = useAuth();
  const canEdit = isAdmin || isStaff;

  const [checklists, setChecklists] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    visible_to: [],
  });

  const fetchChecklists = useCallback(async () => {
    try {
      const res = await checklistsAPI.list(projectId);
      setChecklists(res.data);
    } catch (err) {
      console.error('Failed to fetch checklists:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchChecklists();
    if (canEdit) {
      (isAdmin ? usersAPI.list() : usersAPI.teamMembers())
        .then(res => setAllUsers(res.data)).catch(() => {});
    }
  }, [fetchChecklists, canEdit, isAdmin]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await checklistsAPI.create(projectId, formData);
      setCreateOpen(false);
      setFormData({ title: '', start_date: '', end_date: '', visible_to: [] });
      fetchChecklists();
    } catch (err) {
      console.error('Failed to create checklist:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    if (!activeChecklist) return;
    setFormLoading(true);
    try {
      await checklistsAPI.update(activeChecklist.checklist_id, formData);
      setSettingsOpen(false);
      setActiveChecklist(null);
      fetchChecklists();
    } catch (err) {
      console.error('Failed to update checklist:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (checklistId) => {
    try {
      await checklistsAPI.delete(checklistId);
      fetchChecklists();
    } catch (err) {
      console.error('Failed to delete checklist:', err);
    }
  };

  const openSettings = (cl) => {
    setActiveChecklist(cl);
    setFormData({
      title: cl.title,
      start_date: cl.start_date || '',
      end_date: cl.end_date || '',
      visible_to: cl.visible_to || [],
    });
    setSettingsOpen(true);
  };

  const toggleVisibility = (userId) => {
    setFormData(prev => ({
      ...prev,
      visible_to: prev.visible_to.includes(userId)
        ? prev.visible_to.filter(id => id !== userId)
        : [...prev.visible_to, userId],
    }));
  };

  const clients = allUsers.filter(u => u.role === 'client');

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-muted w-1/3 rounded" />
        <div className="h-48 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-checklists">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Project Checklists</h3>
        {canEdit && (
          <Button
            onClick={() => {
              setFormData({ title: '', start_date: '', end_date: '', visible_to: [] });
              setCreateOpen(true);
            }}
            className="btn-primary"
            data-testid="create-checklist-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Checklist
          </Button>
        )}
      </div>

      {checklists.length === 0 ? (
        <Card>
          <CardContent className="empty-state py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No checklists yet</h3>
            <p className="text-sm text-muted-foreground">
              Create a checklist to track work items across dates
            </p>
          </CardContent>
        </Card>
      ) : (
        checklists.map((cl) => (
          <Card key={cl.checklist_id} data-testid={`checklist-card-${cl.checklist_id}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base font-medium">{cl.title}</CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => exportChecklistToPDF(cl)}
                  title="Export as PDF"
                  data-testid={`export-checklist-${cl.checklist_id}`}
                >
                  <Download className="h-4 w-4" />
                </Button>
                {canEdit && (
                  <>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSettings(cl)} data-testid={`checklist-settings-${cl.checklist_id}`}>
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(cl.checklist_id)}
                      data-testid={`delete-checklist-${cl.checklist_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ChecklistGrid checklist={cl} onRefresh={fetchChecklists} canEdit={canEdit} />
            </CardContent>
          </Card>
        ))
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Checklist</DialogTitle>
            <DialogDescription>Set up a new work tracking checklist for this project</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="form-label">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Period Mar 9-26"
                  required
                  data-testid="checklist-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="form-label">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="checklist-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="checklist-end-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="form-label">
                  <Eye className="h-3.5 w-3.5 inline mr-1" />
                  Client Visibility
                </Label>
                <p className="text-xs text-muted-foreground">Select which clients can view this checklist</p>
                <div className="border border-border rounded-md max-h-36 overflow-y-auto">
                  {clients.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground text-center">No clients available</p>
                  ) : (
                    clients.map(c => (
                      <div key={c.user_id} className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50">
                        <Checkbox
                          id={`vis-${c.user_id}`}
                          checked={formData.visible_to.includes(c.user_id)}
                          onCheckedChange={() => toggleVisibility(c.user_id)}
                        />
                        <Label htmlFor={`vis-${c.user_id}`} className="flex-1 cursor-pointer text-sm font-normal">
                          {c.name} <span className="text-muted-foreground">({c.email})</span>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={formLoading} data-testid="create-checklist-submit">
                {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Checklist'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Checklist Settings</DialogTitle>
            <DialogDescription>Update checklist details and visibility</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSettings}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="form-label">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="edit-checklist-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="form-label">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="edit-checklist-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="edit-checklist-end-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="form-label">
                  <Eye className="h-3.5 w-3.5 inline mr-1" />
                  Client Visibility
                </Label>
                <p className="text-xs text-muted-foreground">Select which clients can view this checklist</p>
                <div className="border border-border rounded-md max-h-36 overflow-y-auto">
                  {clients.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground text-center">No clients available</p>
                  ) : (
                    clients.map(c => (
                      <div key={c.user_id} className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50">
                        <Checkbox
                          id={`edit-vis-${c.user_id}`}
                          checked={formData.visible_to.includes(c.user_id)}
                          onCheckedChange={() => toggleVisibility(c.user_id)}
                        />
                        <Label htmlFor={`edit-vis-${c.user_id}`} className="flex-1 cursor-pointer text-sm font-normal">
                          {c.name} <span className="text-muted-foreground">({c.email})</span>
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={formLoading} data-testid="save-checklist-settings">
                {formLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
